import type * as React from 'react'
import { createContext, useContext, useMemo } from 'react'
import type { JSX } from 'react'

import { useSplit } from '@buffer-mono/features'

import type { PostCard_PostData } from '~publish/components/PostCard/PostCard'
import {
  clearRefreshAttempt,
  hasRefreshAttemptExpired,
} from '~publish/helpers/refreshAttemptTracker'

import type {
  ChannelAction,
  ChannelType,
  EngagementType,
  Service,
} from '../graphqlEnums'
import { useConfigurationQuery } from '../hooks/configuration/useConfigurationQuery'
import type {
  ChannelSettings,
  LikingIcon,
  MediaConfiguration,
  EngagementReactions,
  ReactionConfiguration,
} from '../hooks/configuration/useConfigurationQuery'
import { MENTIONS_CAPABILITIES } from './mentionsCapabilities'
import type { MentionsCapabilityConfig } from './mentionsCapabilities'

// Channels that can use Engage (for banner/switch logic)
const canUseEngage = (channel: Channel): boolean => {
  return (
    (channel.service === 'facebook' && channel.type === 'page') ||
    (channel.service === 'instagram' && channel.type === 'business')
  )
}

// Default character limit fallback when configuration is unavailable
const DEFAULT_CHAR_LIMIT = 1000

// Accept any channel-like object with service and type. `allowedActions`
// is optional here because some lightweight variants of this type — e.g.
// `PostCard_PostData['channel']` — don't request the field. Components
// that gate on `allowedActions` should call `canPerform(channel, action)`,
// which treats missing/empty as deny.
export type Channel =
  | (PostCard_PostData['channel'] & {
      allowedActions?: ReadonlyArray<ChannelAction>
    })
  | {
      id?: string
      service: Service
      type: ChannelType
      allowedActions?: ReadonlyArray<ChannelAction>
    }

/**
 * Comments configuration context providing channel support validation.
 *
 * This context fetches supported channels from the commentsConfiguration API
 * and provides methods to check if a channel is supported for comments.
 *
 * @note Backward compatibility:
 * - `isSupportedInComments` is an alias for `isChannelSupported` (maintained for existing code)
 * - `isSupportedInEngage` always returns false (engage feature is no longer supported)
 * - `canUseEngage` checks if a channel can use the Engage feature (for banner/switch logic)
 */
export type CommentsConfig = {
  isChannelSupported: (channel: Channel) => boolean
  isSupportedInComments: (channel: Channel) => boolean
  isSupportedInEngage: (channel: Channel) => boolean
  canUseEngage: (channel: Channel) => boolean
  hasSupportedCommentChannel: (channels: Channel[]) => boolean
  /**
   * Whether the channel can surface mentions. Matched by channel id, so a
   * channel without one never counts as supported.
   */
  supportsMentions: (channel: Channel) => boolean
  /** Whether at least one of these channels can surface mentions. */
  hasSupportedMentionsChannel: (channels: Channel[]) => boolean
  isRealtimeSync: (channel: Channel) => boolean
  isMentionTypeTextSupported: (channel: Channel) => boolean
  isMentionTypeHandlerSupported: (channel: Channel) => boolean
  supportAIFeatures: (channel: Channel) => boolean
  supportsLiking: (channel: Channel) => boolean
  supportsMultiReactions: (channel: Channel) => boolean
  getLikingIcon: (channel: Channel) => LikingIcon | undefined
  getReactionsConfiguration: (
    channel: Channel,
  ) => ReactionConfiguration | undefined
  getCharacterLimit: (channel: Channel) => number
  getPermanentNote: (channel: Channel) => string | null
  /**
   * Media support for one channel and engagement type. Keyed on `channelId`
   * rather than service + channelType like the accessors above — Instagram's
   * `facebookLogin` and `instagramLogin` variants share both but differ on
   * media support.
   */
  getMediaConfiguration: (
    channelId: string,
    engagementType: EngagementType,
  ) => MediaConfiguration | undefined
  /**
   * Whether liking is blocked until the channel reconnects with the reaction
   * scope. Keyed on `channelId` + `engagementType` like `getMediaConfiguration`
   * — the requirement is per engagement type, the granted scope is per channel.
   */
  reactionsNeedAuthorization: (
    channelId: string,
    engagementType: EngagementType,
  ) => boolean
  /**
   * What a channel offers for an engagement type: the reactions and the icon
   * shown at rest. `undefined`, or an empty reaction list, is how a caller
   * decides to render no control. Keyed like `reactionsNeedAuthorization` —
   * `getReactionsConfiguration` cannot answer this, because it reshapes the
   * comment entry alone.
   */
  getEngagementReactions: (
    channelId: string,
    engagementType: EngagementType,
  ) => EngagementReactions | undefined
  channelNeedsRefresh: (channel: Channel) => boolean
  channelRefreshFailed: (channel: Channel) => boolean
  /**
   * Whether the channel's `mention` feature stays blocked until the channel
   * reconnects. Separate from `channelNeedsRefresh`, which answers for
   * comments — the two features hold their own authorization statuses.
   */
  channelNeedsRefreshForMentions: (channel: Channel) => boolean
  channelsSettings: ChannelSettings[] | null
  /**
   * Per-channel capability bundle for the Mentions inbox. Distinct from
   * `isMentionType{Text,Handler}Supported` above, which describe `@handle`
   * syntax in reply text.
   */
  mentions: MentionsCapabilityConfig
}

type ChannelConfig = {
  config: CommentsConfig
  loading: boolean
  error: string | null
}

const CommunityConfigContext = createContext<ChannelConfig | undefined>(
  undefined,
)

function isChannelSupported(
  channel: Channel,
  channelsSettings: ChannelSettings[] | null,
): boolean {
  if (!channelsSettings) return false
  return channelsSettings.some(
    (config) =>
      config.supportedChannel.service === channel.service &&
      config.supportedChannel.type === channel.type,
  )
}

function CommunityConfigProvider({
  children,
}: {
  children: React.ReactNode
}): JSX.Element {
  const {
    channelsSettings,
    channelCharacterLimits,
    channelAuthorizationStatus,
    channelReactionAuthorization,
    channelReactionSupport,
    channelMediaSupport,
    mentionsSupportedChannelIds,
    mentionsNeedsRefreshChannelIds,
    loading,
    error,
  } = useConfigurationQuery()

  const { config: needsRefreshConfig } = useSplit<Record<string, string>>(
    'geid-cet-needsRefresh',
  )

  const value = useMemo<ChannelConfig>(() => {
    const checkIsSupported = (channel: Channel): boolean =>
      isChannelSupported(channel, channelsSettings)

    const getChannelSettings = (
      channel: Channel,
    ): ChannelSettings | undefined =>
      channelsSettings?.find(
        (setting) =>
          setting.supportedChannel.service === channel.service &&
          setting.supportedChannel.type === channel.type,
      )

    const supportsMentions = (channel: Channel): boolean =>
      channel.id != null &&
      (mentionsSupportedChannelIds?.includes(channel.id) ?? false)

    // Check if a channel uses realtime sync (webhook or firehose)
    const isRealtimeSync = (channel: Channel): boolean => {
      const channelConfig = getChannelSettings(channel)
      return (
        channelConfig?.syncType === 'webhook' ||
        channelConfig?.syncType === 'firehose'
      )
    }

    const isMentionTypeTextSupported = (channel: Channel): boolean => {
      const channelConfig = getChannelSettings(channel)
      return channelConfig?.mentionType === 'text'
    }

    const isMentionTypeHandlerSupported = (channel: Channel): boolean => {
      const channelConfig = getChannelSettings(channel)
      return channelConfig?.mentionType === 'handler'
    }

    const supportAIFeatures = (channel: Channel): boolean => {
      const channelConfig = getChannelSettings(channel)
      return channelConfig?.supportAIFeatures || false
    }

    const supportsLiking = (channel: Channel): boolean => {
      const channelConfig = getChannelSettings(channel)
      return channelConfig?.liking?.supportLiking ?? false
    }

    const supportsMultiReactions = (channel: Channel): boolean => {
      const supportedReactions =
        getReactionsConfiguration(channel)?.supportedReactions
      return (supportedReactions?.length ?? 0) > 1
    }

    const getLikingIcon = (channel: Channel): LikingIcon | undefined => {
      const channelConfig = getChannelSettings(channel)
      return channelConfig?.liking?.icon ?? undefined
    }

    const getReactionsConfiguration = (
      channel: Channel,
    ): ReactionConfiguration | undefined => {
      const channelConfig = getChannelSettings(channel)
      return channelConfig?.liking?.reactionsConfiguration ?? undefined
    }

    const getCharacterLimit = (channel: Channel): number => {
      const channelCharacterLimit = channelCharacterLimits?.find(
        (limit) => limit.id === channel.id,
      )
      return channelCharacterLimit?.limit ?? DEFAULT_CHAR_LIMIT
    }

    const getPermanentNote = (channel: Channel): string | null => {
      const channelConfig = getChannelSettings(channel)
      return channelConfig?.permanentNote ?? null
    }

    const getMediaConfiguration = (
      channelId: string,
      engagementType: EngagementType,
    ): MediaConfiguration | undefined =>
      channelMediaSupport?.find(
        (support) =>
          support.channelId === channelId &&
          support.engagementType === engagementType,
      )?.media

    const reactionsNeedAuthorization = (
      channelId: string,
      engagementType: EngagementType,
    ): boolean =>
      channelReactionAuthorization?.find(
        (entry) =>
          entry.channelId === channelId &&
          entry.engagementType === engagementType,
      )?.needsAuthorization ?? false

    const getEngagementReactions = (
      channelId: string,
      engagementType: EngagementType,
    ): EngagementReactions | undefined =>
      channelReactionSupport?.find(
        (entry) =>
          entry.channelId === channelId &&
          entry.engagementType === engagementType,
      )

    // Matched by channel id like `supportsMentions`: a channel without an id
    // never reads as blocked.
    const channelNeedsRefreshForMentions = (channel: Channel): boolean =>
      channel.id != null &&
      (mentionsNeedsRefreshChannelIds?.includes(channel.id) ?? false)

    const channelNeedsRefresh = (channel: Channel): boolean => {
      const apiVersion =
        'apiVersion' in channel ? channel.apiVersion : undefined

      let configKey: string = channel.service
      if (channel.service === 'instagram' && apiVersion === 'instagramLogin') {
        configKey = 'instagram-instagramLogin'
      } else if (channel.service === 'linkedin') {
        configKey = `linkedin-${channel.type}`
      }

      const isEnabledForChannel = needsRefreshConfig?.[configKey] === 'true'

      if (isEnabledForChannel) {
        const channelAuth = channelAuthorizationStatus?.find(
          (auth) => auth.id === channel.id,
        )
        if (!channelAuth) return false

        return channelAuth.accessTo.some(
          (access) =>
            access.status === 'needsRefresh' ||
            access.status === 'notEnoughData',
        )
      }

      return 'isDisconnected' in channel && !!channel.isDisconnected
    }

    // A refresh was unsuccessful if the channel still needs refresh AND a
    // refresh was attempted more than 5 minutes ago (enough time for the
    // backend to have resolved it if it was going to). When the channel
    // recovers (status returns to ok), we clear the stored attempt so the
    // state resets for future failures.
    const channelRefreshFailed = (channel: Channel): boolean => {
      const id = 'id' in channel ? channel.id : undefined
      if (!id) return false

      const stillNeedsRefresh = channelNeedsRefresh(channel)
      if (!stillNeedsRefresh) {
        clearRefreshAttempt(id)
        return false
      }

      return hasRefreshAttemptExpired(id)
    }

    return {
      config: {
        isChannelSupported: checkIsSupported,
        isSupportedInComments: checkIsSupported, // Alias for backward compatibility
        isSupportedInEngage: (): boolean => false, // Engage is no longer supported, always return false
        canUseEngage,
        hasSupportedCommentChannel: (channels: Channel[]): boolean =>
          channels.some((channel) =>
            isChannelSupported(channel, channelsSettings),
          ),
        supportsMentions,
        hasSupportedMentionsChannel: (channels: Channel[]): boolean =>
          channels.some(supportsMentions),
        isRealtimeSync,
        isMentionTypeTextSupported,
        isMentionTypeHandlerSupported,
        supportAIFeatures,
        supportsLiking,
        supportsMultiReactions,
        getLikingIcon,
        getReactionsConfiguration,
        getCharacterLimit,
        getPermanentNote,
        getMediaConfiguration,
        reactionsNeedAuthorization,
        getEngagementReactions,
        channelNeedsRefresh,
        channelRefreshFailed,
        channelNeedsRefreshForMentions,
        channelsSettings,
        mentions: MENTIONS_CAPABILITIES,
      },
      loading,
      error,
    }
  }, [
    channelsSettings,
    channelCharacterLimits,
    channelAuthorizationStatus,
    channelReactionAuthorization,
    channelReactionSupport,
    channelMediaSupport,
    mentionsSupportedChannelIds,
    mentionsNeedsRefreshChannelIds,
    needsRefreshConfig,
    loading,
    error,
  ])

  return (
    <CommunityConfigContext.Provider value={value}>
      {children}
    </CommunityConfigContext.Provider>
  )
}

function useCommunityConfig(): ChannelConfig {
  const context = useContext(CommunityConfigContext)

  if (!context) {
    throw new Error(
      'useCommunityConfig must be used within CommunityConfigProvider',
    )
  }

  return context
}

export { useCommunityConfig, CommunityConfigProvider, CommunityConfigContext }
